import { graphql, useStaticQuery } from 'gatsby';
import { SnipcartContext } from 'gatsby-plugin-snipcart-advanced/context';
import { SEOContext } from 'gatsby-plugin-wpgraphql-seo';
import React, { useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import LazyLoad from 'react-lazyload';

import ColorContext from '../../context/ColorContext';
import LanguageContext from '../../context/LanguageContext';
import { Box, Flex } from '../Core';
import Footer from '../Footer';
import InstagramFeed from '../InstagramFeed/InstagramFeed';
import Menu from '../Menu';
import Icon from '../SVG';
import ButtonWhatsApp from '../ButtonWhatsApp';

interface ILayoutProps {
  currentLanguage: IPageContext['currentLanguage'];
  currentPath: string | undefined | null;
  hideNewsletter?: boolean;
  menuData: IPageContext['menus'];
  siteSettings: IPageContext['siteSettings'];
  submenuItems: IPageContext['submenuItems'];
  theme?: 'institute' | 'kine';
  translations: NonNullable<
    Queries.PageDAccueilQuery['wpPage']
  >['translations'];
}

const Layout: FCWithClassName<ILayoutProps> = (props) => {
  const { theme = 'kine' } = props;
  const {
    wp: { defaultLanguage, seo },
  } = useStaticQuery(graphql`
    query SiteInfo {
      wp {
        defaultLanguage {
          code
          locale
          slug
        }
        seo {
          contentTypes {
            post {
              title
              schemaType
              metaRobotsNoindex
              metaDesc
            }
            page {
              metaDesc
              metaRobotsNoindex
              schemaType
              title
            }
          }
          webmaster {
            googleVerify
            yandexVerify
            msVerify
            baiduVerify
          }
          schema {
            companyName
            personName
            companyOrPerson
            wordpressSiteName
            siteName
            inLanguage
            logo {
              sourceUrl
              mediaItemUrl
              altText
            }
          }
          social {
            facebook {
              url
              defaultImage {
                sourceUrl
                mediaItemUrl
              }
            }
            instagram {
              url
            }
            linkedIn {
              url
            }
            mySpace {
              url
            }
            pinterest {
              url
              metaTag
            }
            twitter {
              username
            }
            wikipedia {
              url
            }
            youTube {
              url
            }
          }
        }
      }
    }
  `);
  const { changeLanguage } = useContext<{
    changeLanguage: (language: string) => void;
  }>(SnipcartContext);

  useEffect(() => {
    changeLanguage(props.currentLanguage.slug);
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage: props.currentLanguage,
        defaultLanguage,
        translations: props.translations,
      }}
    >
      <ColorContext.Provider
        value={{
          theme,
        }}
      >
        <SEOContext.Provider value={{ global: seo }}>
          <Flex
            direction="column"
            className="relative justify-between min-h-screen text-text z-1"
          >
            <Helmet>
              <meta
                httpEquiv="Content-Security-Policy"
                content="upgrade-insecure-requests"
              />
            </Helmet>
            <Menu
              currentLanguage={props.currentLanguage}
              currentPath={props.currentPath}
              isInstitute={props.theme === 'institute'}
              menuData={props.menuData}
              showLanguageSelector={props.siteSettings?.showLanguageSelector}
              languageSelectorInfoMsg={
                props.siteSettings?.pageNotInOtherLanguages
              }
              translations={props.translations}
              submenuItems={props.submenuItems}
              showSnipcart={props.theme === 'institute'}
            />
            <Box className="w-[15rem] sm:w-[20rem] lg:h-[25rem] xl:w-[25rem] mt-[1rem] sm:-mt-[5rem] lg:-mt-[8rem] xl:-mt-[12rem] -ml-[1rem] fixed -z-[1]">
              <Icon icon="u" className="text-gray-200" />
            </Box>
            <Box className="relative w-full px-4 py-0 mx-auto custom-container lg:px-10">
              {props.children}
              <LazyLoad offset={1000} height={1000} once>
                <InstagramFeed
                  heading={props.siteSettings.instagram?.titre}
                  text={props.siteSettings.instagram?.description}
                  account={theme}
                />
              </LazyLoad>
            </Box>

            <ButtonWhatsApp />

            <Footer
              currentLanguageCode={props.currentLanguage?.code}
              menuItems={props.menuData['footer']}
              siteSettings={props.siteSettings}
              submenuItems={props.submenuItems}
            />
          </Flex>
        </SEOContext.Provider>
      </ColorContext.Provider>
    </LanguageContext.Provider>
  );
};

export default Layout;
